import { isMobile, preventOneWordInLastLine } from '@lemonade-hq/cdk';
import { useI18nContext, useUserContext } from '@lemonade-hq/nuiza';
import type { Seconds } from '@lemonade-hq/ts-helpers';
import { ModalType, useNavContext, useUniclientLayoutContext } from '@lemonade-hq/uniclient-app';
import { PredefinedMenuOptionType } from '@lemonade-hq/uniclient-chat';
import type {
  HeaderAction,
  HeaderActionType,
  LevelOneProgressItem,
  UniclientAction,
  UniclientCoInsurance,
  UniclientHeaderMobileCenter,
  UniclientHeaderTitle,
  UniclientProgress,
} from '@lemonade-hq/uniclient-chat';
import { isNativeApp, nativeApp } from '@lemonade-hq/uniclient-mobile-bridge';
import type { IconName } from '@lemonade-hq/uniclient-theme';
import {
  Button,
  Card,
  Drawer,
  drawerTopSafeAreaHeightVar,
  Flex,
  foundations,
  headerHeightVar,
  hideFocus,
  Icon,
  IconButton,
  Layout,
  Logo,
  SmallLogo,
  spacing,
  Text,
} from '@lemonade-hq/uniclient-theme';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import type {
  HeaderPredefinedMenuActions,
  PredefinedHeaderAction,
  PredefinedHeaderActionSection,
} from '../hooks/useWizardMenu';
import { shouldShowNativeAction, useWizardMenu } from '../hooks/useWizardMenu';
import type { WizardStep } from '../hooks/useWizardResolvedSteps';
import { CoInsurance } from './CoInsurance';
import { FreeTextInputButton } from './components/freeTextInput/FreeTextInputButton';
import { MOBILE_BOTTOM_SAFE_AREA } from './layout';
import { WizardLink } from './WizardLink';
import * as styles from './WizardMobileHeader.css';
import { WizardMobileProgress } from './WizardMobileProgress';
import { parseItemId } from './WizardProgress/utils';

const MOBILE_HEADER_ICON_BUTTON_SIZE = 'md';
const ENTER_DURATION: Seconds = 0.3;
const EXIT_DURATION: Seconds = 0.2;
const enterTransition = { duration: ENTER_DURATION };
const exitTransition = { duration: EXIT_DURATION };
const delay = ENTER_DURATION;

const menuVariants = {
  hidden: { opacity: 0, y: 30, transition: enterTransition },
  visible: { opacity: 1, y: 0, transition: { ...enterTransition, delay } },
  exit: { opacity: 0, y: 10, transition: exitTransition },
};

const wrapperVariants = {
  hidden: { opacity: 0, transition: enterTransition },
  visible: { opacity: 1, transition: { ...enterTransition, delay } },
  exit: { opacity: 0, transition: exitTransition },
};

const PADDING_BLOCK_END = isNativeApp() ? `${MOBILE_BOTTOM_SAFE_AREA}px` : spacing.s16;

interface MenuItemButtonProps {
  readonly action: PredefinedHeaderAction;
  readonly onInvoke: (action: PredefinedHeaderAction) => Promise<void>;
  readonly key: string;
}

const MenuItemButton: React.FC<MenuItemButtonProps> = ({ action, key, onInvoke }) => {
  const handleClick = useCallback(() => {
    void onInvoke(action);
  }, [action, onInvoke]);

  return (
    <Flex
      alignItems="center"
      as="button"
      className={styles.action}
      disabled={action.disabled}
      gap={spacing.s12}
      height={spacing.s48}
      key={key}
      onClick={handleClick}
      px={0}
      py={spacing.s12}
      role="menuitem"
      type="button"
    >
      {action.icon && <Icon name={action.icon} size={MOBILE_HEADER_ICON_BUTTON_SIZE} />}
      <Text className={styles.actionText} type="bodyMedium">
        {action.title}
      </Text>
    </Flex>
  );
};

// TODO: remove and use `nativeOpenExitDialogAction` instead
const NativeExitAction: React.FC = () => {
  const {
    modal: {
      [ModalType.Exit]: { title, subtitle, acceptAction },
    },
  } = useUniclientLayoutContext();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Drawer.Root onOpenChange={setIsOpen} open={isOpen}>
      <Drawer.Trigger>
        <IconButton as="div" className={clsx(hideFocus)} icon="close" size={MOBILE_HEADER_ICON_BUTTON_SIZE} />
      </Drawer.Trigger>
      <Drawer.Content description={subtitle} paddingBlockEnd={PADDING_BLOCK_END} title={title}>
        <Flex flexDirection="column" gap={spacing.s32}>
          <Flex alignItems="center" flexDirection="column" gap={spacing.s08} textAlign="center">
            <Text type="h5">{title}</Text>
            <Text type="bodyMedium">{preventOneWordInLastLine(subtitle)}</Text>
          </Flex>
          <Button
            onClick={() => {
              void nativeApp.dismiss({
                type: 'redirectToHome',
                data: {},
              });
            }}
            type="button"
            variant="secondary"
          >
            {acceptAction}
          </Button>
        </Flex>
      </Drawer.Content>
    </Drawer.Root>
  );
};

interface HeaderMobileMenuProps {
  readonly subActions: (PredefinedHeaderAction | PredefinedHeaderActionSection)[];
  readonly icon: IconName;
}

const HeaderMobileMenu: React.FC<HeaderMobileMenuProps> = ({ subActions, icon }) => {
  const { s } = useI18nContext();
  const [isOpen, setIsOpen] = useState(false);

  const invokeAction = useCallback(async (action: PredefinedHeaderAction) => {
    if (action.disabled) return;

    setIsOpen(false);
    await action.onAction?.();
  }, []);

  const hasSections = subActions.some(item => 'actions' in item);

  return (
    <Drawer.Root dismissible={false} modal={false} onOpenChange={setIsOpen} open={isOpen}>
      <Drawer.Trigger>
        <IconButton
          as="div"
          background="transparent"
          className={hideFocus}
          icon={icon}
          size={MOBILE_HEADER_ICON_BUTTON_SIZE}
        />
      </Drawer.Trigger>
      <Drawer.Content
        className={styles.drawerMenuContent}
        description={s('wizard_mobile_header.title')}
        fullViewport
        resetDefaultStyling
        title={s('wizard_mobile_header.title')}
      >
        <AnimatePresence>
          {isOpen && (
            <motion.div animate="visible" exit="exit" initial="hidden" key="drawer-content" variants={wrapperVariants}>
              <Flex flexDirection="column">
                <Flex alignItems="center" className={styles.menuHeader} justifyContent="space-between" width="100%">
                  <Layout width={spacing.s40} />
                  <Logo className={styles.fullLogo} />
                  <IconButton background="transparent" icon="close" onClick={() => setIsOpen(false)} size="lg" />
                </Flex>

                <Flex
                  animate="visible"
                  as={motion.div}
                  className={styles.menu}
                  exit="exit"
                  flexDirection="column"
                  {...(hasSections && { gap: spacing.s40 })}
                  initial="hidden"
                  role="menu"
                  variants={menuVariants}
                >
                  {subActions.map(item => {
                    if ('actions' in item) {
                      return (
                        <Flex flexDirection="column" gap={spacing.s12} key={`section-${item.title}`}>
                          <Text type="h5">{item.title}</Text>
                          <Flex flexDirection="column">
                            {item.actions.map(subAction => (
                              <MenuItemButton
                                action={subAction}
                                key={`${item.title}-${subAction.title}`}
                                onInvoke={invokeAction}
                              />
                            ))}
                          </Flex>
                        </Flex>
                      );
                    } else {
                      return <MenuItemButton action={item} key={item.title} onInvoke={invokeAction} />;
                    }
                  })}
                </Flex>
              </Flex>
            </motion.div>
          )}
        </AnimatePresence>
      </Drawer.Content>
    </Drawer.Root>
  );
};

interface HeaderMobileActionProps {
  readonly action:
    | HeaderActionType
    | PredefinedMenuOptionType.Exit
    | PredefinedMenuOptionType.FreeTextInput
    | PredefinedMenuOptionType.ImmediateExit;
  readonly predefinedActions: HeaderPredefinedMenuActions;
  readonly performAction: (action?: UniclientAction | undefined) => Promise<unknown>;
}

const HeaderMobileAction = forwardRef<HTMLDivElement, HeaderMobileActionProps>(
  ({ action, predefinedActions, performAction }, ref) => {
    const isDesktop = useMemo(() => !isNativeApp() && !isMobile(), []);
    const menuSubActions = useWizardMenu({
      predefinedActions,
      subActions:
        action !== PredefinedMenuOptionType.Exit &&
        action !== PredefinedMenuOptionType.ImmediateExit &&
        action !== PredefinedMenuOptionType.FreeTextInput &&
        'subActions' in action
          ? action.subActions
          : [],
      performAction,
    });

    let component = null;
    if (action === PredefinedMenuOptionType.Exit) {
      component = <NativeExitAction />;
    } else if (action === PredefinedMenuOptionType.ImmediateExit) {
      component = (
        <IconButton
          className={clsx(hideFocus)}
          icon="close"
          onClick={() => void nativeApp.dismiss()}
          size={MOBILE_HEADER_ICON_BUTTON_SIZE}
        />
      );
    } else if (action === PredefinedMenuOptionType.FreeTextInput) {
      return null;
    } else if ('subActions' in action) {
      if (menuSubActions.length === 0) return null;

      component = <HeaderMobileMenu icon={action.icon} subActions={menuSubActions} />;
    } else if (action.disabledOnDesktop && isDesktop) {
      return null;
    } else {
      component = (
        <IconButton
          icon={action.icon}
          onClick={() => void performAction(action as UniclientAction)}
          size={MOBILE_HEADER_ICON_BUTTON_SIZE}
        />
      );
    }

    return (
      <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }} layout ref={ref}>
        {component}
      </motion.div>
    );
  },
);

type HeaderMobileActionsProps = Omit<HeaderMobileActionProps, 'action'> & {
  readonly actions?: (HeaderActionType | PredefinedMenuOptionType.FreeTextInput)[];
  readonly showFreeTextInput?: boolean;
  readonly setShowFreeTextInput?: (showFreeTextInput: boolean) => void;
};

const HeaderMobileActions: React.FC<HeaderMobileActionsProps> = ({
  actions = [],
  predefinedActions,
  performAction,
  showFreeTextInput,
  setShowFreeTextInput,
}) => {
  return (
    <Flex gap={spacing.s08}>
      <AnimatePresence>
        {actions.map(action => {
          const key =
            action === PredefinedMenuOptionType.FreeTextInput
              ? 'freeTextInput'
              : typeof action === 'object' && 'subActions' in action
                ? 'menu'
                : typeof action === 'object'
                  ? action.icon
                  : String(action);

          if (action === PredefinedMenuOptionType.FreeTextInput) {
            return (
              <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }} key={key} layout>
                <FreeTextInputButton
                  setShowFreeTextInput={setShowFreeTextInput}
                  showFreeTextInput={showFreeTextInput}
                  size={MOBILE_HEADER_ICON_BUTTON_SIZE}
                />
              </motion.div>
            );
          }

          return (
            <HeaderMobileAction
              action={action}
              key={key}
              performAction={performAction}
              predefinedActions={predefinedActions}
            />
          );
        })}
      </AnimatePresence>
    </Flex>
  );
};

interface HeaderProps {
  readonly actions?: (HeaderActionType | PredefinedMenuOptionType.FreeTextInput)[];
  readonly currentStepIndex: number;
  readonly logoAction?: HeaderAction | PredefinedMenuOptionType.Exit | PredefinedMenuOptionType.ImmediateExit | null;
  readonly predefinedActions: HeaderPredefinedMenuActions;
  readonly performAction: (action?: UniclientAction | undefined) => Promise<unknown>;
  readonly progress?: UniclientProgress; // TODO: add progress bar / script title
  readonly title?: UniclientHeaderTitle;
  readonly steps: WizardStep[];
  readonly mobileCenter?: UniclientHeaderMobileCenter;
  readonly hideLogo?: boolean;
  readonly showFreeTextInput?: boolean;
  readonly setShowFreeTextInput?: (showFreeTextInput: boolean) => void;
  readonly coInsurance?: UniclientCoInsurance;
}

interface ProgressItemWithMetadata extends LevelOneProgressItem {
  // eslint-disable-next-line functional/prefer-readonly-type
  stepsCount: number;
  readonly index: number;
  readonly parentId: string | null;
  readonly level: number;
}

interface LogoComponentProps {
  readonly coInsurance?: UniclientCoInsurance;
  readonly consumerUrl: string;
  readonly displaySmallLogo: boolean;
  readonly homeUrl: string;
  readonly isLogged: boolean;
}

const LogoComponent: React.FC<LogoComponentProps> = ({
  displaySmallLogo,
  homeUrl,
  isLogged,
  consumerUrl,
  coInsurance,
}) => {
  const hasCoInsurance = coInsurance != null && coInsurance.position === 'top';

  return (
    <Flex
      alignItems="center"
      animate={{ opacity: 1 }}
      as={motion.div}
      exit={{ opacity: 0 }}
      gap={spacing.s04}
      height="40px"
      initial={{ opacity: 0 }}
      key={displaySmallLogo ? 'smallLogo' : 'bigLogo'}
      p={spacing.s12}
    >
      <WizardLink href={isLogged ? consumerUrl : homeUrl} pr={hasCoInsurance ? spacing.s02 : undefined}>
        {displaySmallLogo ? <SmallLogo /> : <Logo className={styles.fullLogo} />}
      </WizardLink>
      {hasCoInsurance && <CoInsurance coInsurance={coInsurance} isMobile />}
    </Flex>
  );
};

interface TitleComponentProps {
  readonly title: UniclientHeaderTitle;
}

const TitleComponent: React.FC<TitleComponentProps> = ({ title }) => {
  return (
    <Flex
      alignItems="center"
      animate={{ opacity: 1 }}
      as={motion.div}
      exit={{ opacity: 0 }}
      gap={spacing.s04}
      initial={{ opacity: 0 }}
    >
      {typeof title === 'object' && <Icon color="primary" name={title.icon} size="md" />}
      <Text className={styles.title} type="bodyMedium">
        {typeof title === 'string' ? title : title.text}
      </Text>
    </Flex>
  );
};

interface MobileHeaderCenterNavBarProps {
  readonly mobileCenter?: UniclientHeaderMobileCenter;
  readonly title?: UniclientHeaderTitle;
  readonly progress?: UniclientProgress;
  readonly currentStepIndex: number;
  readonly progressStepMap: ProgressItemWithMetadata[];
  readonly performAction: (action?: UniclientAction | undefined) => Promise<unknown>;
  readonly homeUrl: string;
  readonly consumerUrl: string;
  readonly isLogged: boolean;
  readonly hideLogo?: boolean;
}

const MobileHeaderCenterNavBar: React.FC<MobileHeaderCenterNavBarProps> = ({
  mobileCenter,
  title,
  progress,
  currentStepIndex,
  progressStepMap,
  performAction,
  homeUrl,
  consumerUrl,
  isLogged,
  hideLogo,
}) => {
  let content = null;

  if (mobileCenter === 'title' && title != null) {
    content = <TitleComponent title={title} />;
  } else if (mobileCenter === 'progress' && progress != null) {
    content = (
      <WizardMobileProgress
        currentItemId={progress.currentItemId}
        currentStepIndex={currentStepIndex}
        items={progressStepMap}
        onAction={performAction}
      />
    );
  } else if (mobileCenter === 'logo' && !hideLogo) {
    content = (
      <LogoComponent consumerUrl={consumerUrl} displaySmallLogo={false} homeUrl={homeUrl} isLogged={isLogged} />
    );
  } else if (title != null) {
    content = <TitleComponent title={title} />;
  } else if (progress != null) {
    content = (
      <WizardMobileProgress
        currentItemId={progress.currentItemId}
        currentStepIndex={currentStepIndex}
        items={progressStepMap}
        onAction={performAction}
      />
    );
  }

  return content;
};

export const WizardMobileHeader: React.FC<HeaderProps> = ({
  actions,
  currentStepIndex,
  logoAction,
  performAction,
  predefinedActions,
  progress,
  title,
  steps,
  mobileCenter,
  hideLogo,
  showFreeTextInput,
  setShowFreeTextInput,
  coInsurance,
}) => {
  const {
    nav: { homeUrl, consumerUrl },
  } = useNavContext();
  const { user } = useUserContext();
  const isLogged = user != null;

  const progressStepMap = useMemo(() => {
    if (progress == null) return [];

    type FlatItemMap = Record<string, ProgressItemWithMetadata>;

    const createFlatItemMap = (
      items: UniclientProgress['items'],
      parentId: string | null = null,
      level: number = 1,
    ): FlatItemMap => {
      return items.reduce<FlatItemMap>((flatMap, item, index) => {
        // Add current item with metadata
        flatMap[item.id] = {
          ...item,
          stepsCount: 0,
          index,
          parentId,
          level,
        };

        // Process nested items if they exist
        if (item.subItems && item.subItems.length > 0) {
          const nestedItems = createFlatItemMap(item.subItems, item.id, level + 1);
          return { ...flatMap, ...nestedItems };
        }

        return flatMap;
      }, {});
    };

    // Create initial map with all items from all three levels
    const initialMap = createFlatItemMap(progress.items);

    // Count steps for each item
    const map = steps.reduce((acc, step) => {
      if (step.frame?.progress?.currentItemId != null) {
        const { itemId: currentItemId } = parseItemId(step.frame.progress.currentItemId);

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (acc[currentItemId] != null) {
          acc[currentItemId].stepsCount += 1;
        }
      }

      return acc;
    }, initialMap);

    // Return only top-level items for main display, sorted by index
    // Note: You can modify this to return all items or filter differently if needed
    return Object.values(map)
      .filter(item => item.level === 1)
      .sort((a, b) => a.index - b.index);
  }, [progress, steps]);

  const showLogoAction =
    logoAction != null &&
    shouldShowNativeAction(
      logoAction === PredefinedMenuOptionType.Exit || logoAction === PredefinedMenuOptionType.ImmediateExit
        ? ({ nativeMobileOnly: true, icon: 'close' } as HeaderAction)
        : logoAction,
    );

  const displaySmallLogo = progress != null || title != null || (coInsurance != null && coInsurance.position === 'top');

  return (
    <>
      {/* Moving the shadow of the header to a separate layout to support top drawer */}
      <Layout
        className={foundations({ shadow: 'xs' })}
        display={{ sm: 'flex', lg: 'none' }}
        height={`calc(${headerHeightVar} + ${drawerTopSafeAreaHeightVar})`}
        position="fixed"
        top={0}
        width="100%"
      />
      <Layout
        alignItems="center"
        as={Card}
        className={styles.header}
        display={{ sm: 'flex', lg: 'none' }}
        flexDirection="row"
        justifyContent="space-between"
        pb={spacing.s14}
        px={spacing.s12}
      >
        <Flex alignItems="center" position="relative" width="100%">
          <Flex flex={1} justifyContent="flex-start">
            <AnimatePresence mode="popLayout">
              {showLogoAction ? (
                <HeaderMobileAction
                  action={logoAction}
                  performAction={performAction}
                  predefinedActions={predefinedActions}
                />
              ) : (
                mobileCenter !== 'logo' &&
                !hideLogo && (
                  <LogoComponent
                    coInsurance={coInsurance}
                    consumerUrl={consumerUrl}
                    displaySmallLogo={displaySmallLogo}
                    homeUrl={homeUrl}
                    isLogged={isLogged}
                  />
                )
              )}
            </AnimatePresence>
          </Flex>
          <Flex className={styles.progressContainer} justifyContent="center" left="50%" position="absolute">
            <AnimatePresence>
              <MobileHeaderCenterNavBar
                consumerUrl={consumerUrl}
                currentStepIndex={currentStepIndex}
                hideLogo={hideLogo}
                homeUrl={homeUrl}
                isLogged={isLogged}
                mobileCenter={mobileCenter}
                performAction={performAction}
                progress={progress}
                progressStepMap={progressStepMap}
                title={title}
              />
            </AnimatePresence>
          </Flex>
          <Flex flex={1} />
          <HeaderMobileActions
            actions={actions}
            performAction={performAction}
            predefinedActions={predefinedActions}
            setShowFreeTextInput={setShowFreeTextInput}
            showFreeTextInput={showFreeTextInput}
          />
        </Flex>
      </Layout>
    </>
  );
};
